
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, NavLink, Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import Navigation from './Navigation';
import { GlobalTicker } from './GlobalTicker';
import { NAV_ITEMS } from '../../constants';
import { useData } from '../../hooks/useData';
import { AppLogo } from '../ui/AppLogo';
import { dataManager } from '../../utils/dataManager';
import { Users, User } from 'lucide-react';

export const AppLayout: React.FC = () => {
  const location = useLocation();
  const isTv = location.pathname === '/tv';
  const isMatch = location.pathname === '/match';
  const isResults = location.pathname === '/results';
  
  const theme = useData(() => dataManager.getTheme());
  const isLight = theme === 'light';
  const gameMode = useData(() => dataManager.getGameMode());

  const showMobileNav = !isTv && !isMatch;

  // Smart Nav Logic
  const [showNav, setShowNav] = useState(true);
  const scrollRef = useRef<HTMLElement>(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const currentScrollY = scrollRef.current.scrollTop;
      const diff = currentScrollY - lastScrollY.current;
      
      // Threshold to prevent jitter
      if (Math.abs(diff) < 10) return;

      // Always show at the very top
      if (currentScrollY < 20) {
         setShowNav(true);
      } else if (diff > 0) {
         // Scrolling Down -> Hide
         setShowNav(false);
      } else {
         // Scrolling Up -> Show
         setShowNav(true);
      }
      
      lastScrollY.current = currentScrollY;
    };

    const container = scrollRef.current;
    if (container) {
        container.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
        if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (isTv) {
    return (
      <div className="h-screen w-screen overflow-hidden bg-kfppc-void">
         <Outlet />
         <GlobalTicker />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col relative overflow-hidden bg-kfppc-void transition-colors duration-500 text-kfppc-white">
      
      <div className="absolute inset-0 z-0 pointer-events-none select-none overflow-hidden">
         <div className="absolute inset-0 bg-kfppc-void transition-colors duration-500" />
         <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("https://www.transparenttextures.com/patterns/carbon-fibre.png")` }} />
         <div className="absolute inset-0 flex items-center justify-center opacity-[0.05]">
            <svg width="100%" height="100%" viewBox="0 0 200 100" preserveAspectRatio="none">
               <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                 <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.1" className="text-kfppc-white" />
               </pattern>
               <rect width="200" height="100" fill="url(#grid)" />
            </svg>
         </div>
         <div className="absolute inset-0 bg-gradient-to-r from-black/5 via-transparent to-black/5" />
      </div>

      {/* Desktop Header */}
      <header className="hidden md:flex shrink-0 z-40 w-full items-center justify-between py-1.5 px-4 lg:px-6 4xl:py-6 4xl:px-12 transition-all duration-300 min-h-[64px] 4xl:min-h-[160px] border-b border-kfppc-white/10 shadow-[0_5px_40px_-10px_rgba(0,0,0,0.05)] bg-kfppc-surface/95 backdrop-blur-3xl relative">
          <Link to="/" className="flex items-center gap-3 4xl:gap-10 hover:opacity-90 transition-opacity cursor-pointer">
             <AppLogo className="w-10 h-10 4xl:w-32 4xl:h-32" />
             <div className="flex flex-col justify-center">
               <div className="flex items-center gap-2 w-full opacity-90 mb-0.5">
                 <span className="text-[8px] 4xl:text-sm font-bold text-kfppc-lavender tracking-[0.4em] uppercase leading-none text-shadow-sm whitespace-nowrap">
                   Kepnes Family
                 </span>
                 <div className="h-px bg-gradient-to-r from-kfppc-lavender/40 to-transparent w-12 4xl:w-32 rounded-full"></div>
               </div>
               <h1 className="text-2xl lg:text-3xl 4xl:text-6xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-kfppc-white via-kfppc-royal to-kfppc-white drop-shadow-[0_0_25px_rgba(0,51,160,0.2)] leading-none uppercase italic">
                 Ping Pong Central
               </h1>
             </div>
          </Link>

          <div className="flex items-center gap-4">
            
            {/* Mode Switcher */}
            <div className={cn("p-0.5 rounded-full flex items-center border transition-all", isLight ? "bg-kfppc-void border-kfppc-royal/20" : "bg-black/20 border-white/10")}>
                <button 
                  onClick={() => dataManager.setGameMode('singles')}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all",
                    gameMode === 'singles' 
                      ? (isLight ? "bg-kfppc-royal text-white shadow-md" : "bg-kfppc-royal text-white shadow-lg") 
                      : "text-kfppc-muted hover:text-kfppc-white"
                  )}
                >
                   <User size={12} /> <span className="hidden xl:inline">Singles</span>
                </button>
                <button 
                  onClick={() => dataManager.setGameMode('doubles')}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all",
                    gameMode === 'doubles' 
                      ? (isLight ? "bg-kfppc-royal text-white shadow-md" : "bg-kfppc-royal text-white shadow-lg") 
                      : "text-kfppc-muted hover:text-kfppc-white"
                  )}
                >
                   <Users size={12} /> <span className="hidden xl:inline">Doubles</span>
                </button>
            </div>

            <nav className="flex items-center gap-1 bg-kfppc-void/10 p-1 4xl:p-3 rounded-full border border-kfppc-white/5 backdrop-blur-md no-scrollbar max-w-[50%] lg:max-w-none">
                {NAV_ITEMS.map((item) => (
                <NavLink
                    key={item.path}
                    to={item.path}
                    title={item.label}
                    className={({ isActive }) => cn(
                    "flex items-center gap-2 px-3 py-1.5 4xl:px-8 4xl:py-4 rounded-full transition-all duration-300 group relative",
                    isActive 
                        ? "bg-kfppc-royal text-[#FFFFFF] shadow-lg" 
                        : "text-kfppc-muted hover:text-kfppc-white hover:bg-kfppc-white/5"
                    )}
                >
                    {({ isActive }) => (
                    <>
                        <item.icon size={16} strokeWidth={isActive ? 2.5 : 2} className={cn(isActive ? "fill-current/20" : "", "4xl:w-8 4xl:h-8")} />
                        <span className={cn(
                        "text-[10px] 4xl:text-lg font-bold uppercase tracking-wide whitespace-nowrap",
                        "hidden xl:inline"
                        )}>
                        {item.label === 'Tournament History' ? 'Tournaments' : item.label}
                        </span>
                        
                        <div className={cn(
                            "absolute top-full mt-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-kfppc-surface/95 backdrop-blur-md text-kfppc-white text-[10px] 4xl:text-xs font-black uppercase tracking-widest rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap border border-white/10 shadow-xl z-50 translate-y-1 group-hover:translate-y-0",
                            "xl:hidden"
                        )}>
                            {item.label}
                            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-kfppc-surface/95 border-t border-l border-white/10 transform rotate-45" />
                        </div>
                    </>
                    )}
                </NavLink>
                ))}
            </nav>
          </div>
      </header>

      {/* FIXED Mobile Header (Logo + Mode Switcher) */}
      <div className={cn(
          "md:hidden fixed top-0 left-0 right-0 z-40 w-full flex items-center justify-between py-2 px-4 border-b border-kfppc-white/10 bg-kfppc-surface/80 backdrop-blur-xl transition-transform duration-300 ease-in-out",
          showNav ? "translate-y-0" : "-translate-y-full"
      )}>
          <Link to="/" className="flex items-center gap-3">
             <AppLogo className="w-8 h-8" />
             <div className="flex flex-col">
                <h1 className="text-lg font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-kfppc-white via-kfppc-royal to-kfppc-white uppercase italic leading-none">
                    KFPPC
                </h1>
             </div>
          </Link>

          <div className={cn("p-0.5 rounded-full flex items-center border transition-all", isLight ? "bg-kfppc-void border-kfppc-royal/20" : "bg-black/20 border-white/10")}>
                <button 
                  onClick={() => dataManager.setGameMode('singles')}
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full transition-all",
                    gameMode === 'singles' 
                      ? (isLight ? "bg-kfppc-royal text-white shadow-md" : "bg-kfppc-royal text-white shadow-lg") 
                      : "text-kfppc-muted hover:text-kfppc-white"
                  )}
                >
                   <User size={14} />
                </button>
                <button 
                  onClick={() => dataManager.setGameMode('doubles')}
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full transition-all",
                    gameMode === 'doubles' 
                      ? (isLight ? "bg-kfppc-royal text-white shadow-md" : "bg-kfppc-royal text-white shadow-lg") 
                      : "text-kfppc-muted hover:text-kfppc-white"
                  )}
                >
                   <Users size={14} />
                </button>
            </div>
      </div>

      <div className="flex-1 relative overflow-hidden flex flex-col z-10">
        <main
            ref={scrollRef}
            className={cn(
              "flex-grow w-full mx-auto relative no-scrollbar",
              (isMatch || isResults) ? "overflow-hidden" : "overflow-y-auto"
            )}
        >
           <div className={cn(
             // Use h-full for bracket/results/match views to enable full expansion
             // min-h-full for other views to allow natural content height
             (isResults || isMatch) ? "h-full" : "min-h-full",
             // Adjusted padding for mobile:
             // Use minimal padding on mobile since we have the fixed mobile header.
             // Increased pt to prevent content hiding behind fixed header initially.
             (isMatch || isResults)
                ? "p-0 pt-16 md:pt-2 lg:pt-4"
                : "p-4 pt-20 md:pt-6 4xl:p-12",
             // Bottom Padding for Nav - minimal for results/match views to maximize content space
             // Match page gets pb-0 to maximize viewport (GlobalTicker is positioned separately)
             (isResults || isMatch)
                ? "pb-0"
                : showMobileNav ? "pb-32 md:pb-6" : "pb-6 md:pb-6",
             "max-w-[3840px] mx-auto flex flex-col"
           )}>
             <Outlet />
             {/* Spacer to account for fixed bottom UI (Nav) on mobile - Conditionally rendered */}
             {showMobileNav && <div className="shrink-0 w-full h-32 md:hidden block" />}
           </div>
        </main>

        {/* Global Ticker positioned via component-internal fixed style */}
        <div className="hidden md:block">
            <GlobalTicker />
        </div>
      </div>

      {showMobileNav && (
         <div className={cn(
             "md:hidden fixed bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)] bg-kfppc-void transition-transform duration-300 ease-in-out",
             showNav ? "translate-y-0" : "translate-y-full"
         )}>
           <Navigation />
         </div>
      )}
    </div>
  );
};
